import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Avatar, Checkbox, Button } from '@mui/material';
import placeholderImage from 'images/placeholder-grey-image.jpg'; // Adjust path as needed
import avatarPlaceholder from 'images/avatar-cropped.png'; // Adjust path as needed
import { lighten } from '@mui/system'; // Import the lighten utility
import { MoreHorizIcon } from 'icons/materialIcons';
import { useTeam } from 'team/contexts/teamContext';
import { useAuth } from 'contexts/AuthContext';
import TeamMemberCardOptionsModal from './TeamMemberCardOptionsModal';
import { downloadQRCode } from 'apis/qrCodeApi';
import TeamMemberTemplateButton from './TeamMemberTemplateButton';
import TeamMemberSubteamButton from './TeamMemberSubteamButton';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import EditAdminStatusModal from './EditAdminStatusModal';
import { updateTeamMemberRole } from 'team/apis/teamApi';


const TeamMemberCard = ({ member, onEditCardClick, isSelected = false, onSelectionChange = () => { }, teamId }) => {
    const { authState } = useAuth();
    const { currentTeamId, teams } = useTeam();
    const currentTeam = teams[currentTeamId];
    const isCurrentUserAdmin = currentTeam?.membershipInfo?.role === 'admin' || currentTeam?.membershipInfo?.role === 'owner' || currentTeam?.membershipInfo?.role === 'teamOwner' || currentTeam?.membershipInfo?.role === 'team_owner';
    const [fullName, setFullName] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [company, setCompany] = useState('');
    const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    const [cardTheme, setCardTheme] = useState('');
    const [cardName, setCardName] = useState('');
    const lightenedCardTheme = cardTheme ? lighten(cardTheme, 0.7) : '#fff';
    const effectiveTeamId = teamId || currentTeamId;
    const optionsButtonRef = useRef(null);
    const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
    const [isEditAdminModalOpen, setIsEditAdminModalOpen] = useState(false);
    const [memberRole, setMemberRole] = useState(member?.role || 'member');
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });



    useEffect(() => {
        if (member) {
            setMemberRole(member.role || 'member');
            console.log('TeamMemberCard - Member data:', {
                _id: member._id,
                userId: member.userId,
                role: member.role,
                user: member.user,
                defaultCard: member.defaultCard
            });

            // ✅ Use defaultCard data instead of user data
            if (member.defaultCard) {
                console.log('TeamMemberCard - Using defaultCard data:', member.defaultCard);

                setFullName(member.defaultCard.contact_info?.fullName || member.user?.fullName || '');
                setCardName(member.defaultCard.cardName || member.defaultCard.contact_info?.fullName || member.user?.fullName || '');
                setJobTitle(member.defaultCard.contact_info?.jobTitle || '');
                setCompany(member.defaultCard.contact_info?.company || '');
                setCoverPhotoUrl(member.defaultCard.coverPhotoUrl || null);
                setCompanyLogoUrl(member.defaultCard.companyLogoUrl || null);
                setProfilePictureUrl(member.defaultCard.profilePictureUrl || member.user?.profilePictureUrl || null);
                setCardTheme(member.defaultCard.cardTheme || '');
            } else {
                console.log('TeamMemberCard - No defaultCard, using user data as fallback');

                // Fallback to user data if no defaultCard
                // If no user data either, use userId as fallback name
                const fallbackName = member.user?.fullName ||
                    member.user?.email?.split('@')[0] ||
                    member.userId ||
                    'Unknown User';

                setFullName(fallbackName);
                setCardName(fallbackName);
                setJobTitle('');
                setCompany('');
                setCoverPhotoUrl(member.user?.profilePictureUrl || null);
                setCompanyLogoUrl(null);
                setProfilePictureUrl(member.user?.profilePictureUrl || null);
                setCardTheme('');
            }

            // Debug image URLs
            console.log('TeamMemberCard - Image URLs:', {
                coverPhotoUrl: member.defaultCard?.coverPhotoUrl,
                companyLogoUrl: member.defaultCard?.companyLogoUrl,
                profilePictureUrl: member.defaultCard?.profilePictureUrl || member.user?.profilePictureUrl
            });
        }
    }, [member]);

    const memberCardId = member?.defaultCard?._id;


    const handleMoreMenuClick = (event) => {
        event.stopPropagation();
        setIsOptionsModalOpen(true);
    };

    const handleOptionsModalClose = () => {
        setIsOptionsModalOpen(false);
    };

    const handleNotificationClose = () => {
        setNotification((prev) => ({ ...prev, open: false }));
    };

    const handleDownloadQrCode = async () => {
        if (!memberCardId) {
            setNotification({
                open: true,
                message: 'No default card found for this member',
                severity: 'error'
            });
            return;
        }

        try {
            const cardUrl = `${window.location.origin}/card/${memberCardId}?source=qr_code`;
            await downloadQRCode(cardUrl);
            setIsOptionsModalOpen(false);
        } catch (error) {
            console.error('Error downloading QR code:', error);
            setNotification({
                open: true,
                message: 'Failed to download QR code',
                severity: 'error'
            });
        }
    };

    const handleViewCard = () => {
        if (!memberCardId) {
            setNotification({
                open: true,
                message: 'No default card found for this member',
                severity: 'error'
            });
            return;
        }

        const cardUrl = `${window.location.origin}/card/${memberCardId}?source=team_member_card`;
        window.open(cardUrl, '_blank', 'noopener,noreferrer');
        setIsOptionsModalOpen(false);
    };
    const handleEditAdminStatus = () => {
        setIsEditAdminModalOpen(true);
        setIsOptionsModalOpen(false);
    };

    const handleApplyRole = async (newRole) => {
        try {
            const teamIdToUse = teamId || currentTeamId;
            const result = await updateTeamMemberRole(teamIdToUse, member.userId, newRole);

            if (result.success) {
                setNotification({
                    open: true,
                    message: `Member role updated to ${newRole} successfully`,
                    severity: 'success'
                });

                // Refresh member role in the current UI
                member.role = newRole;
                setMemberRole(newRole);
            }
        } catch (error) {
            setNotification({
                open: true,
                message: error.message || 'Failed to update member role',
                severity: 'error'
            });
        }
    };

    // Removed share and edit button handlers for simplified metrics UI

    const getJobTitleAndCompany = () => {
        if (jobTitle && company) {
            return `${jobTitle} at ${company}`;
        }
        return jobTitle || company || '';
    };

    const getEmail = () => {
        if (member?.user?.isCompanyPage) {
            return 'Company Page';
        }
        // ✅ Use defaultCard email first, then fallback to user email
        return member?.defaultCard?.contact_info?.email || member?.user?.email || '';
    };

    return (
        <Box
            onClick={() => onEditCardClick(member)}
            sx={{
                cursor: 'pointer',
                padding: '0.3125rem',
                backgroundColor: lightenedCardTheme || '#fff',
                borderRadius: '0.625rem',
                boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
                width: '100%',
                maxWidth: '32%',
                minHeight: '16.25rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxSizing: 'border-box',
                marginRight: '1rem',
                marginBottom: '1rem',
                position: 'relative',
            }}
        >
            {/* Checkbox in top-left corner */}
            <Checkbox
                checked={isSelected}
                onChange={(e) => {
                    e.stopPropagation();
                    onSelectionChange(e.target.checked);
                }}
                sx={{
                    position: 'absolute',
                    top: '0.5rem',
                    left: '0.5rem',
                    zIndex: 1,
                    color: '#e0e0e0',
                    padding: '0',
                    margin: '0',
                    '&.Mui-checked': {
                        color: '#000',
                    },
                    '& .MuiSvgIcon-root': {
                        borderRadius: '0.5rem',
                    },
                }}
            />
            <Button
                ref={optionsButtonRef}
                onClick={handleMoreMenuClick}
                sx={{
                    position: 'absolute',
                    top: '0.8rem',
                    right: '0.8rem',
                    zIndex: 1,
                    minWidth: '3rem',
                    height: '1.75rem',
                    padding: '0 0.6875rem',
                    borderRadius: '999px',
                    backgroundColor: '#ffffff',
                    color: '#4f4f4f',
                    boxShadow: 'none',
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: '#f5f5f5',
                        boxShadow: 'none',
                    },
                }}
                aria-label="Member actions"
            >
                <MoreHorizIcon sx={{ fontSize: '1.25rem' }} />
            </Button>
            <Box
                sx={{
                    width: '100%',
                    height: '7.5rem',
                    backgroundImage: `url(${coverPhotoUrl || placeholderImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderTopLeftRadius: '0.625rem',
                    borderTopRightRadius: '0.625rem',
                    borderBottomLeftRadius: '0.625rem',
                    borderBottomRightRadius: '0.625rem',
                    position: 'relative',
                    marginBottom: '0.75rem',
                }}
            >
                <Avatar
                    src={profilePictureUrl || avatarPlaceholder}
                    sx={{
                        width: '6rem',
                        height: '6rem',
                        backgroundColor: '#ffffff',
                        position: 'absolute',
                        bottom: '-2.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        border: '0.25rem solid #ffffff',
                    }}
                />

                {companyLogoUrl && (
                    <Avatar
                        src={companyLogoUrl}
                        sx={{
                            width: '2rem',
                            height: '2rem',
                            position: 'absolute',
                            bottom: '-1.625rem', // Adjusts position relative to profile picture
                            right: '40%', // Adjusts position relative to profile picture
                            transform: 'translateX(50%) translateY(25%)', // Positions logo slightly overlapping profile picture
                            backgroundColor: '#ffffff',
                        }}
                    />
                )}
            </Box>

            <Typography
                variant="h6"
                sx={{
                    marginTop: '2.25rem',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    color: '#000',
                    marginBottom: '0.25rem',
                }}
            >
                {fullName || cardName || 'Card Title'}
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    fontSize: '0.8rem',
                    color: '#555',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '0.375rem',
                }}
            >
                {getJobTitleAndCompany()}
            </Typography>

            {getEmail() && (
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.75rem',
                        color: '#888',
                        fontWeight: 500,
                        textAlign: 'center',
                        marginBottom: '0.75rem',
                        wordBreak: 'break-all'
                    }}
                >
                    {getEmail()}
                </Typography>
            )}

            {/* Template + Subteam Section */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    marginBottom: '0.75rem',
                    gap: '8px'
                }}
            >
                <TeamMemberTemplateButton member={member} teamId={effectiveTeamId} />
                <TeamMemberSubteamButton member={member} teamId={effectiveTeamId} />
            </Box>

            <TeamMemberCardOptionsModal
                open={isOptionsModalOpen}
                onClose={handleOptionsModalClose}
                anchorEl={optionsButtonRef.current}
                onDownloadQrCode={handleDownloadQrCode}
                onViewCard={handleViewCard}
                onEditAdminStatus={handleEditAdminStatus}
                showAdminRole={isCurrentUserAdmin && !(
                    memberRole === 'owner' || 
                    memberRole === 'teamOwner' || 
                    memberRole === 'team_owner' || 
                    member?.isOwner || 
                    member?.isTeamOwner || 
                    member?.membershipInfo?.role === 'owner' || 
                    member?.membershipInfo?.role === 'teamOwner' || 
                    member?.user?.role === 'owner' || 
                    member?.user?.role === 'teamOwner' || 
                    member?.user?.role === 'team_owner' || 
                    member?.userId === authState?.user?.userId ||
                    member?.userId === currentTeam?.membershipInfo?.userId || 
                    member?.userId === teams[currentTeamId]?.membershipInfo?.userId ||
                    member?.userId === currentTeam?.userId ||
                    member?.userId === teams[teamId]?.userId
                )}
            />

            <EditAdminStatusModal
                open={isEditAdminModalOpen}
                onClose={() => setIsEditAdminModalOpen(false)}
                member={member}
                onApply={handleApplyRole}
            />

            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />

        </Box>
    );
};

export default TeamMemberCard;